import {
    categoryLayoutVariantDefs,
    filterRoofIdsBySide,
    ONE_DRAW_DEFAULT_MAXFILL_CATEGORY,
} from '../../../../services/layoutVariantPrompts';
import {
    captureOrthoSnapshotFromViewer,
    isValidSnapshotDataUrl,
} from '../../../../helpers/layoutSnapshotCapture';
import { useViewerStore } from '../../../../store/store';
import type { SavedRoofModulesLayout } from '../../../../@types/types';
import {
    addModulesToAllRoofsMaxFill,
    addModulesToRoofsByIds,
    removeGreyModulesAndSingleModuleRoofs,
    SetModuleGraph,
} from '../Three-JS-Viewer/Controllers/ModuleControl';
import { captureLiveModulesLayout } from '../Three-JS-Viewer/Controllers/Common';
import { clearModulesOnRoofFaceIds } from '../Three-JS-Viewer/Controllers/DefaultLayoutControl';
import {
    beginBatchLayoutSiteCalc,
    endBatchLayoutSiteCalc,
    flushVariantLayoutSiteCalc,
} from './utils/batchLayoutSiteCalc';
import { countModulesInLayout } from './utils/aiDesignFlowDebug';
import { resolveLayoutKeyForCategory } from './utils/categoryBestVariantScoring';
import { countRoofsWithModulesFromRoofInfo } from './utils/layout';

export { resolveLayoutKeyForCategory };

function indexVariantModulesLayouts(
    into: Record<string, SavedRoofModulesLayout>,
    category: string,
    variants: Array<{ id?: string; modulesLayout?: SavedRoofModulesLayout }>,
): void {
    for (const variant of variants ?? []) {
        if (!variant.id || !Array.isArray(variant.modulesLayout) || !variant.modulesLayout.length) {
            continue;
        }
        into[`variant:${category}:${variant.id}`] = variant.modulesLayout;
    }
}

type RunGenerateAllCategoryLayoutVariantsActionArgs = {
    sceneViewer: any;
    roofCombinationsFromAi: Array<{ name: string; roofFaceIds?: string[] }>;
    defaultMaxfillRoofIds: string[];
    bestRoofsForLayout: { roofFaceIds?: string[] };
    selectedRoofCombinationKey: string;
    setAiLoadingStep?: (step: string) => void;
};

/** After Gemini returns roof combos, generate per-category “more layout” variants on the correct roof sets. */
export async function runGenerateAllCategoryLayoutVariantsAction(
    args: RunGenerateAllCategoryLayoutVariantsActionArgs,
): Promise<void> {
    const {
        sceneViewer,
        roofCombinationsFromAi,
        defaultMaxfillRoofIds,
        bestRoofsForLayout,
        selectedRoofCombinationKey,
        setAiLoadingStep,
    } = args;

    const categories = Object.keys(categoryLayoutVariantDefs);
    if (!sceneViewer?.roofMeshArr?.length || categories.length === 0) return;

    setAiLoadingStep?.('Generating layout variants per category...');

    const batchedVariants: Record<string, any[]> = {};

    for (let i = 0; i < categories.length; i++) {
        const categoryLabel = categories[i];
        setAiLoadingStep?.(
            `Generating layout variants (${i + 1}/${categories.length}): ${categoryLabel}...`,
        );
        const layoutKey = resolveLayoutKeyForCategory(categoryLabel, roofCombinationsFromAi);
        const variants = await runGenerateCategoryLayoutVariantsAction({
            sceneViewer,
            categoryLabel,
            layoutKey,
            roofCombinationsFromAi,
            defaultMaxfillRoofIds,
            bestRoofsForLayout,
            layoutCandidatesByCategory: {},
            selectedRoofCombinationKey,
            deferStoreWrite: true,
        });
        if (Array.isArray(variants) && variants.length > 0) {
            batchedVariants[categoryLabel] = variants;
        }
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }

    useViewerStore.setState((prev: any) => {
        const nextModulesLayoutByKey = { ...(prev.layoutModulesLayoutByKey || {}) };
        for (const [category, rows] of Object.entries(batchedVariants)) {
            indexVariantModulesLayouts(nextModulesLayoutByKey, category, rows ?? []);
        }
        return {
            aiCategoryLayoutVariants: batchedVariants,
            layoutModulesLayoutByKey: nextModulesLayoutByKey,
        };
    });
}

type RunGenerateCategoryLayoutVariantsActionArgs = {
    sceneViewer: any;
    categoryLabel: string;
    layoutKey: string;
    roofCombinationsFromAi: any[];
    defaultMaxfillRoofIds: string[];
    bestRoofsForLayout: { roofFaceIds?: string[] };
    layoutCandidatesByCategory: Record<string, unknown>;
    selectedRoofCombinationKey: string;
    /** When true, return variants without writing `aiCategoryLayoutVariants` (caller batches one store write). */
    deferStoreWrite?: boolean;
};

/** Generate and cache layout variants for a single AI design category (e.g. Maximum Efficiency). */
export async function runGenerateCategoryLayoutVariantsAction(
    args: RunGenerateCategoryLayoutVariantsActionArgs,
): Promise<any[] | void> {
    const {
        sceneViewer: self,
        categoryLabel,
        layoutKey,
        roofCombinationsFromAi,
        defaultMaxfillRoofIds,
        bestRoofsForLayout,
        layoutCandidatesByCategory,
        selectedRoofCombinationKey,
        deferStoreWrite = false,
    } = args;

    if (!self?.roofMeshArr?.length) return deferStoreWrite ? [] : undefined;

    beginBatchLayoutSiteCalc();
    try {
        const viewerAllRoofIds: string[] = (self.roofMeshArr ?? [])
            .map((r: any) => r.roofFaceId)
            .filter(Boolean);
        const defaultIds =
            (defaultMaxfillRoofIds?.length ?? 0) > 0
                ? defaultMaxfillRoofIds
                : (bestRoofsForLayout.roofFaceIds ?? []);

        const originalLayoutKey = selectedRoofCombinationKey ?? 'default';
        const originalModuleDir = (self as any)?.moduleDir;
        const originalModuleDirSafe =
            originalModuleDir === 'port' || originalModuleDir === 'land' ? originalModuleDir : null;
        const originalRoofFaceIds: string[] = (() => {
            if (originalLayoutKey === 'default') return defaultIds;
            const ids = roofCombinationsFromAi.find((c: any) => c.name === originalLayoutKey)?.roofFaceIds;
            return Array.isArray(ids) && ids.length > 0 ? ids : defaultIds;
        })();

        const categoryCandidates = layoutCandidatesByCategory?.[categoryLabel] as
            | { key?: string }[]
            | undefined;
        const categoryMainKey = categoryCandidates?.[0]?.key;
        const baseLayoutKey =
            layoutKey === 'default' && categoryLabel !== ONE_DRAW_DEFAULT_MAXFILL_CATEGORY
                ? (categoryMainKey ?? layoutKey)
                : layoutKey;

        let roofFaceIds: string[] = [];
        if (baseLayoutKey === 'default') {
            roofFaceIds =
                categoryLabel === ONE_DRAW_DEFAULT_MAXFILL_CATEGORY ? viewerAllRoofIds : defaultIds;
        } else {
            const combo = roofCombinationsFromAi.find((c: any) => c.name === baseLayoutKey);
            roofFaceIds = combo?.roofFaceIds ?? defaultIds;
        }
        if (!roofFaceIds?.length) return deferStoreWrite ? [] : undefined;

        const allRoofIds: string[] = viewerAllRoofIds;
        const allowLowEfficiencyPanels = categoryLabel === ONE_DRAW_DEFAULT_MAXFILL_CATEGORY;
        const variants: any[] = [];

        const runVariant = async (
            label: string,
            dir: 'port' | 'land' | 'both' | null,
            minEffThreshold?: number | null,
            side?: 'north' | 'south' | 'east' | 'west' | 'front' | null,
        ) => {
            await new Promise<void>((resolve) => setTimeout(resolve, 0));
            const effectiveIds = side
                ? filterRoofIdsBySide(self.roofMeshArr, allRoofIds, side)
                : roofFaceIds;
            const roofIdsForVariant = allowLowEfficiencyPanels ? allRoofIds : effectiveIds;
            if (!effectiveIds.length && !allowLowEfficiencyPanels) {
                variants.push({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    label,
                    roofIds: [],
                    dir,
                    minEff: minEffThreshold ?? 0,
                    productionW: 0,
                    modules: 0,
                    efficiency: null,
                    solarAccess: null,
                    multiplier: null,
                    snapshotUrl: undefined,
                    snapshotCaptureError: 'No roofs available for this layout variant',
                });
                return;
            }
            if (allowLowEfficiencyPanels) {
                addModulesToAllRoofsMaxFill(self, null, dir);
                await SetModuleGraph(self);
            } else {
                clearModulesOnRoofFaceIds(self, effectiveIds);
                addModulesToRoofsByIds(self, effectiveIds, dir as any);
                await flushVariantLayoutSiteCalc(self);
                if (minEffThreshold != null && minEffThreshold > 0) {
                    removeGreyModulesAndSingleModuleRoofs(self, effectiveIds, minEffThreshold);
                    await flushVariantLayoutSiteCalc(self);
                }
            }

            let snapshotUrl: string | undefined;
            let snapshotCaptureError: string | undefined;
            const captured = await captureOrthoSnapshotFromViewer(self);
            if (captured.url) {
                snapshotUrl = captured.url;
            } else {
                snapshotCaptureError = captured.error ?? 'Snapshot capture failed';
            }

            const modulesLayout = captureLiveModulesLayout(self) as SavedRoofModulesLayout;
            const layoutModuleCount = countModulesInLayout(modulesLayout);

            const readVariantMetrics = () => {
                const stateNow = useViewerStore.getState() as any;
                return {
                    w: typeof stateNow.actualWattage === 'number' ? stateNow.actualWattage : 0,
                    m:
                        layoutModuleCount ||
                        (typeof stateNow.modeCount === 'number' ? stateNow.modeCount : 0),
                    eff: typeof stateNow.efficiency === 'number' ? stateNow.efficiency : null,
                    solar:
                        typeof stateNow.weightedSolarAverage === 'number'
                            ? stateNow.weightedSolarAverage
                            : null,
                    mult:
                        typeof stateNow.moduleMultiplierAverage === 'number' &&
                        Number.isFinite(stateNow.moduleMultiplierAverage)
                            ? stateNow.moduleMultiplierAverage
                            : null,
                };
            };

            let metrics = readVariantMetrics();
            if (metrics.m > 0 && metrics.w <= 0) {
                await flushVariantLayoutSiteCalc(self);
                metrics = readVariantMetrics();
            }

            const { w, m, eff, solar, mult } = metrics;
            const stateNow = useViewerStore.getState() as any;
            const roofCount = countRoofsWithModulesFromRoofInfo(
                stateNow.roofInfoArr,
                allowLowEfficiencyPanels ? allRoofIds : effectiveIds,
            );

            const variantId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const variantRow: Record<string, unknown> = {
                id: variantId,
                label,
                roofIds: roofIdsForVariant,
                dir,
                minEff: minEffThreshold ?? 0,
                productionW: w,
                modules: m,
                efficiency: eff,
                solarAccess: solar,
                multiplier: mult,
                roofCount,
                snapshotUrl,
                snapshotCaptureError: snapshotUrl ? undefined : snapshotCaptureError,
            };
            if (layoutModuleCount > 0) {
                variantRow.modulesLayout = modulesLayout;
            }
            variants.push(variantRow);

            if (isValidSnapshotDataUrl(snapshotUrl)) {
                const snapshotKey = JSON.stringify({
                    label,
                    roofIds: roofIdsForVariant,
                    dir: dir ?? null,
                });
                useViewerStore.setState((prev: any) => ({
                    aiLayoutVariantSnapshots: {
                        ...(prev.aiLayoutVariantSnapshots || {}),
                        [snapshotKey]: snapshotUrl,
                    },
                }));
            }
        };

        const defs =
            categoryLayoutVariantDefs[categoryLabel] ??
            categoryLayoutVariantDefs['Max Production with Best Roofs'];
        for (const v of defs) {
            await runVariant(v.label, v.dir, v.minEff, v.side);
        }

        if (originalLayoutKey === 'default') {
            addModulesToAllRoofsMaxFill(self, null, originalModuleDirSafe);
            await SetModuleGraph(self);
        } else {
            addModulesToRoofsByIds(self, originalRoofFaceIds, originalModuleDirSafe as any);
            await SetModuleGraph(self);
            removeGreyModulesAndSingleModuleRoofs(self, originalRoofFaceIds);
            await SetModuleGraph(self);
        }

        if (deferStoreWrite) {
            return variants;
        }

        useViewerStore.setState((prev: any) => {
            const nextModulesLayoutByKey = { ...(prev.layoutModulesLayoutByKey || {}) };
            indexVariantModulesLayouts(nextModulesLayoutByKey, categoryLabel, variants);
            return {
                aiCategoryLayoutVariants: {
                    ...(prev.aiCategoryLayoutVariants || {}),
                    [categoryLabel]: variants,
                },
                layoutModulesLayoutByKey: nextModulesLayoutByKey,
            };
        });
    } finally {
        await endBatchLayoutSiteCalc(self);
    }
}
